import React from "react";
import { ColorV2 } from "config/colors";
import { CustomIcon } from "components/icon/customIcon.component";
import {
  CalendarIcon,
  FieldWrapper,
  IconButton,
  IconRow,
  Input,
  Label,
  Message,
  RequiredMark,
} from "./datePicker.styles";
import { DatePickerSize, DatePickerState } from "./datePicker.types";

export interface DatePickerInputProps {
  labelText?: string;
  required?: boolean;
  size: DatePickerSize;
  visualState: DatePickerState;
  isOpen: boolean;
  value: string;
  placeholderText: string;
  disabled?: boolean;
  readOnly?: boolean;
  showClearButton: boolean;
  messageText?: string;
  onClear: (event?: React.MouseEvent) => void;
  onToggleOpen: () => void;
  onFocus: () => void;
  onBlur: () => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  inputRef: React.RefObject<HTMLInputElement>;
  clearLabel: string;
  openLabel: string;
}

/** Renders the date picker input, label, icons, and helper message. */
export const DatePickerInput = (props: DatePickerInputProps): JSX.Element => {
  return (
    <>
      {props.labelText && (
        <Label>
          {props.labelText}
          {props.required && <RequiredMark>*</RequiredMark>}
        </Label>
      )}
      <FieldWrapper $state={props.visualState} $size={props.size}>
        <Input
          ref={props.inputRef}
          type="text"
          value={props.value}
          placeholder={props.placeholderText}
          onClick={props.onToggleOpen}
          onFocus={props.onFocus}
          onBlur={props.onBlur}
          onChange={props.onChange}
          $state={props.visualState}
          $size={props.size}
          disabled={props.disabled}
          readOnly={props.readOnly}
          aria-expanded={props.isOpen}
        />
        <IconRow>
          {props.showClearButton && (
            <IconButton
              type="button"
              onClick={props.disabled ? undefined : props.onClear}
              aria-label={props.clearLabel}
              disabled={props.disabled}
              $disabled={props.disabled}
            >
              <CustomIcon
                name="v2/cross"
                size="16px"
                color={
                  props.visualState === "error"
                    ? ColorV2.NegativeRed
                    : ColorV2.Blue5
                }
              />
            </IconButton>
          )}
          <IconButton
            type="button"
            onClick={props.onToggleOpen}
            aria-label={props.openLabel}
            disabled={props.disabled}
            $disabled={props.disabled}
          >
            <CalendarIcon aria-hidden>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </CalendarIcon>
          </IconButton>
        </IconRow>
      </FieldWrapper>
      {props.messageText && (
        <Message $state={props.visualState}>{props.messageText}</Message>
      )}
    </>
  );
};
